import type { UserProfile, Rival, RivalRequest } from '../../types'
import type { AvatarPresetId } from '../../storage'
import { ProfileAvatar } from '../ProfileAvatar'
import QuestEmptyShield from '../QuestEmptyShield'
import { getClubBanner } from '../../clubBanners'
import type { useScoreCountUp } from './useScoreCountUp'
import { computeTotalPointsMeta } from './totalPointsMeta'

/**
 * The identity card at the top of the profile — avatar, name, rivals count and
 * favourite-team shield — plus the total-points hero beneath it. In 'self' view
 * the avatar, name and shield are all entry points (avatar picker, Settings,
 * team picker); in read-only 'other' view they are inert.
 */
function ProfileHeader({
  profile,
  currentGameweekPoints,
  currentGameweek,
  settledFixtures,
  totalFixtures,
  isReadOnly,
  countUp,
  avatarPreset,
  rivals,
  incomingRivalRequests,
  onBack,
  onOpenSettings,
  onOpenAvatarPicker,
  onOpenTeamPicker,
  onOpenRivalsList,
}: {
  profile: UserProfile
  currentGameweekPoints: number
  currentGameweek: number
  /** Fixtures of the current gameweek whose results are already in, and how
   *  many there are in total — the meta row's only inputs. */
  settledFixtures: number
  totalFixtures: number
  isReadOnly: boolean
  /** The total-points count-up, owned by `Profile` — the records block below the hero waits on
   *  the same signal, and running the hook twice would race two animations. */
  countUp: ReturnType<typeof useScoreCountUp>
  avatarPreset: AvatarPresetId | null
  rivals: Rival[]
  incomingRivalRequests: RivalRequest[]
  onBack?: () => void
  onOpenSettings?: () => void
  onOpenAvatarPicker: () => void
  onOpenTeamPicker: () => void
  onOpenRivalsList: () => void
}) {
  const { displayedScore, isScoreRevealed, isDeltaRevealed } = countUp
  const meta = computeTotalPointsMeta({
    gameweek: currentGameweek,
    settledFixtures,
    totalFixtures,
  })

  return (
    <>
      <div className="profile__header-card">
        {isReadOnly && onBack && (
          <button className="profile__back" onClick={onBack} aria-label="Back">
            ← Back
          </button>
        )}
        <div className="profile__header-row">
          <ProfileAvatar
            preset={avatarPreset}
            initials={profile.initials}
            className="profile__avatar"
            onClick={isReadOnly ? undefined : onOpenAvatarPicker}
          />

          <div className="profile__header-identity">
            {isReadOnly ? (
              <p className="profile__name">{profile.name}</p>
            ) : (
              // The username doubles as the Settings entry point
              // on the own-profile view — the standalone "Settings" button is gone.
              <button
                type="button"
                className="profile__name profile__name-btn"
                onClick={onOpenSettings}
              >
                {profile.name}
              </button>
            )}

            {isReadOnly ? (
              // Other players' rivalry graphs aren't fetched yet — inert placeholder
              // count until that seam is filled in.
              <div className="profile__rivals-count profile__rivals-count--placeholder">
                <span className="profile__rivals-count-item">
                  <strong>—</strong> Rivals
                </span>
              </div>
            ) : (
              <div className="profile__rivals-count">
                <button
                  type="button"
                  className="profile__rivals-count-item"
                  onClick={onOpenRivalsList}
                >
                  <strong>{rivals.length}</strong> Rivals
                  {incomingRivalRequests.length > 0 && (
                    <span className="profile__rivals-badge">
                      {incomingRivalRequests.length}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>

          {isReadOnly ? (
            getClubBanner(profile.favouriteTeam) ? (
              <img
                className="profile__team-banner"
                src={getClubBanner(profile.favouriteTeam)}
                alt=""
                aria-hidden="true"
              />
            ) : (
              <QuestEmptyShield className="profile__team-banner" size={44} />
            )
          ) : (
            <button
              type="button"
              className="profile__team-banner-btn"
              onClick={onOpenTeamPicker}
              aria-label="Change favourite team"
            >
              {getClubBanner(profile.favouriteTeam) ? (
                <img
                  className="profile__team-banner"
                  src={getClubBanner(profile.favouriteTeam)}
                  alt=""
                  aria-hidden="true"
                />
              ) : (
                <QuestEmptyShield className="profile__team-banner" size={44} />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Total-points hero — a typographic block on the page
          gutter, not a card. Row structure is identical in every state: only
          the delta's presence and the meta row's text change, so the numeral
          never moves when a gameweek starts or ends. */}
      <div
        className={`profile__total ${
          isScoreRevealed ? '' : 'profile__total--hidden'
        }`}
      >
        <p className="profile__total-label label-eyebrow">Total points</p>
        <div className="profile__total-value-row">
          <span className="profile__total-value">{displayedScore}</span>
          {/* The delta is a "your current-gameweek progress" affordance —
              meaningless on another player's profile, where it would only leak
              the default `+0` — and its presence is what signals
              a live gameweek, so it is absent (not muted) whenever the
              gameweek isn't in progress. */}
          {!isReadOnly && meta.isLive && (
            <span
              className={`profile__total-delta ${
                isDeltaRevealed ? '' : 'profile__total-delta--hidden'
              }`}
            >
              <span className="profile__total-delta-dot" aria-hidden="true" />
              {currentGameweekPoints >= 0 ? '+' : ''}
              {currentGameweekPoints}
            </span>
          )}
        </div>
        <p className="profile__total-meta">{meta.text}</p>
      </div>

      <hr className="profile__total-rule" />
    </>
  )
}

export default ProfileHeader
